// Lifecycle for client-side vector outline layers (BBB-457).
//
// Kept separate from useLayerController because these are not tile overlays:
// map.overlayMapTypes only accepts MapTypes with a getTile interface, whereas
// these are Polylines attached straight to the map. Draw order comes from each
// polyline's zIndex (derived from the source's precedence) rather than from
// position in the overlay array.
//
// Refresh is driven by the map's `idle` event, not by React state, so panning
// refetches without a re-render on every mouse move.

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { CATALOGUE, getActiveSources, isRenderable } from '../data/catalogue';
import { createVectorOutlineLayer } from './adapters/arcgisVectorOutlines';
import { createSvgHatchLayer } from './adapters/svgHatchOverlay';
import { createHealthTracker } from './layerHealth';
import { beginGesture, recordLayer } from './metrics';
import { reportWarn } from './report';

const DEFAULT_MAX_ZOOM = 20;

/**
 * Which adapter draws this source.
 *
 * Two renderers, one handle shape. `createSvgHatchLayer` fills each polygon with
 * an SVG `<pattern>`; `createVectorOutlineLayer` draws a google.maps.Polygon,
 * which can only take one solid colour and an opacity. Both expose the same
 * `refresh` / `clear` / `destroy`, so the reconcile effect below does not care
 * which it is holding.
 *
 * A source opts in by declaring `hatchDirection`, and that declaration is now the
 * whole decision: the runtime kill switch that used to sit alongside it was
 * deleted on 24/08/2026, so a hatched source has no route back to the Polygon
 * fill. Keeping the choice in the catalogue puts it next to the colour it pairs
 * with, rather than in a list of source ids here that would drift the first time
 * a designation is added. Today that means the three England designations
 * (BBB-457): Scotland and Wales come from WMS as pre-rendered rasters and never
 * reach this controller at all, which is why "don't touch Scotland" is satisfied
 * by construction rather than by a guard.
 *
 * @param {object} source catalogue source
 * @returns {object} layer handle
 */
export function createLayerForSource(source, deps = {}) {
  const wantsHatch = Boolean(source.hatchDirection);
  // `deps` carries the metrics harness's onStats through to whichever adapter
  // runs. The hatch adapter accepts it and does not report, so a hatched layer
  // contributes nothing to a recording. That is a gap, not a bug: the harness is
  // off by default and exists to profile the fetch cost the two adapters share.
  return wantsHatch
    ? createSvgHatchLayer(source, deps)
    : createVectorOutlineLayer(source, deps);
}

/**
 * Which vector sources should be live right now.
 *
 * Pure so it can be asserted without a map — mirrors collectDesiredSources in
 * useLayerController, but filtered to vector sources only.
 *
 * @returns {Array<object>} catalogue sources, in ascending precedence
 */
export function selectVectorSources(layerStates, viewportRegion, zoom) {
  const region =
    viewportRegion === 'england' || viewportRegion === 'scotland' || viewportRegion === 'wales'
      ? viewportRegion
      : null;
  if (!region) return [];

  const selected = [];

  for (const category of CATALOGUE) {
    if (!layerStates[category.id]?.visible) continue;

    for (const source of getActiveSources(category, region)) {
      if (source.kind !== 'arcgis-vector') continue;
      if (!isRenderable(source)) continue;

      // Written as `zoom >= minZoom` rather than `!(zoom < minZoom)` so a
      // source that omits minZoom fails CLOSED, matching collectDesiredSources
      // in useLayerController. The two predicates used to disagree on exactly
      // that omission, and here the open failure mode is the expensive one: a
      // vector source with no zoom floor fetches the whole country.
      const maxZoom = source.maxZoom ?? DEFAULT_MAX_ZOOM;
      const withinZoom = zoom >= source.minZoom && zoom <= maxZoom;
      if (!withinZoom) continue;

      selected.push(source);
    }
  }

  return selected.sort((a, b) => (a.precedence ?? 100) - (b.precedence ?? 100));
}

/**
 * Read a Google Maps LatLngBounds as the plain envelope the adapter expects.
 * Returns null before the map has settled, when getBounds() is undefined.
 */
export function boundsToEnvelope(map) {
  const bounds = map?.getBounds?.();
  if (!bounds) return null;
  const { north, south, east, west } = bounds.toJSON();
  return { north, south, east, west };
}

/**
 * The status one completed refresh should publish.
 *
 * Pure so the mapping can be asserted without a map. 'partial' is deliberately
 * machine-facing: it records only that the adapter hit its page cap and the
 * render is therefore missing designations. What a farmer should be shown for a
 * partial layer — if it should be drawn at all — is still an open decision, so
 * nothing consumes this string yet.
 *
 * null is "leave the status alone", not "say nothing at all": for a refresh that
 * is still the current one, the caller feeds that same outcome to the layer's
 * health tracker, which is what eventually publishes `unavailable` if nothing
 * ever succeeds (BBB-527). It cannot tell failed from superseded on its own,
 * which is why refreshAll decides that before consulting this at all.
 *
 * @param {{truncated: boolean}|undefined} result what handle.refresh resolved to
 * @returns {string|null} status to publish, or null to leave the status alone
 */
export function statusForRefresh(result) {
  // No result means the fetch failed or was superseded; the previous render (and
  // its status) still stands, so claiming anything about it would be a lie.
  if (!result) return null;
  return result.truncated ? 'partial' : 'loaded';
}

/**
 * Hand back a stable array identity for as long as the SET of source ids is
 * unchanged (BBB-523).
 *
 * selectVectorSources builds a fresh array every call, so memoising it on
 * [layerStates, viewportRegion, zoom] still yields a new identity on every zoom
 * step — and on every layerStates update, including the setStatus('loaded') this
 * hook triggers itself. Anything depending on that identity re-ran and refetched
 * even though the selected layers had not changed. Comparing by id means the
 * reconcile effect only fires when a layer is genuinely added or removed;
 * viewport-driven refetching stays with the map's `idle` event.
 *
 * The cache is a ref written during render, matching how desiredRef below is
 * kept in step. Key and array are always swapped together, so a discarded
 * render cannot leave the two disagreeing.
 *
 * @param {Array<object>} sources catalogue sources for this render
 * @returns {Array<object>} the same array as last render if the ids match
 */
function useStableSourceSet(sources) {
  const key = sources.map((source) => source.id).join('|');
  const cache = useRef({ key: null, sources });

  if (cache.current.key !== key) {
    cache.current = { key, sources };
  }

  return cache.current.sources;
}

export function useVectorLayerController({ map, layerStates, viewportRegion, zoom, setStatus }) {
  // sourceId → layer handle. Handles persist across refreshes so a pan reuses
  // them instead of tearing every polyline down and rebuilding.
  const layersRef = useRef(new Map());
  // sourceId → health tracker, created and disposed alongside the handle so a
  // layer's timers live exactly as long as it does. The SAME mechanism the tile
  // controller uses, deliberately: until BBB-527 nothing on this path published
  // `unavailable` at all, so a farmer who toggled SSSI on and had the fetch fail
  // was told nothing and read the empty map as "no designations on my land".
  const trackersRef = useRef(new Map());
  // sourceId → how many refreshes this hook has dispatched for it. A refresh that
  // settles while its source's count has already moved on was superseded by a
  // newer viewport, and must report nothing to the health tracker. See refreshAll.
  //
  // Deliberately never deleted, unlike the handle and the tracker beside it. If a
  // layer toggled off mid-fetch reset to zero, the next toggle-on would reissue
  // the same number the outstanding fetch is holding, and that fetch would then
  // look current and report into a tracker that has since been replaced.
  const generationsRef = useRef(new Map());

  const selected = useMemo(
    () => selectVectorSources(layerStates, viewportRegion, zoom),
    [layerStates, viewportRegion, zoom],
  );
  // Identity only changes when the selected set does — see useStableSourceSet.
  const desired = useStableSourceSet(selected);

  // The idle listener is registered once per map, so it must read the current
  // desired set from a ref rather than closing over a stale value.
  const desiredRef = useRef(desired);
  desiredRef.current = desired;
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  // setStatus goes through a ref for the same reason: refreshAll IS the idle
  // callback, so rebuilding it re-registers the listener and re-runs the
  // reconcile effect — the duplicate fetch this ticket removes, reintroduced by
  // nothing more than a caller passing an inline callback.
  const setStatusRef = useRef(setStatus);
  setStatusRef.current = setStatus;

  const refreshAll = useCallback(() => {
    if (!map) return;
    const envelope = boundsToEnvelope(map);
    if (!envelope) return;

    // One gesture per settled pan or zoom. Layers with no handle yet are
    // excluded from the expected count, or the gesture would never complete and
    // every summary would print as "superseded".
    const live = desiredRef.current.filter((source) => layersRef.current.has(source.id));
    beginGesture({ expected: live.length, zoom: zoomRef.current });

    const generations = generationsRef.current;

    for (const source of live) {
      const handle = layersRef.current.get(source.id);
      const tracker = trackersRef.current.get(source.id);
      // Publishes 'loading' on the first fetch and starts the clocks behind
      // 'slow' and 'unavailable'. Later requests are no-ops inside the tracker,
      // so a pan does not restart them.
      tracker?.requested();

      // Which dispatch this is for this source, so the settlement below can tell
      // "the fetch failed" from "a newer viewport already replaced it". The
      // adapters answer `undefined` to both, and both used to reach failed().
      const generation = (generations.get(source.id) ?? 0) + 1;
      generations.set(source.id, generation);

      // Status has to wait for the fetch: whether the render is whole or only
      // partial is not knowable until the adapter reports back (BBB-522). Not
      // awaited, so the layers still refresh in parallel as they did before.
      //
      // Published through setStatusRef rather than setStatus directly, so this
      // callback does not depend on the caller's function identity — that
      // dependency is what re-registered the idle listener and re-ran the
      // reconcile effect, which is the duplicate fetch BBB-523 removes.
      Promise.resolve(handle.refresh(map, envelope, zoomRef.current))
        .then((result) => {
          // Superseded: this hook has already dispatched a newer refresh for
          // this source, so whatever came back describes a viewport the farmer
          // has left. Say NOTHING. The layer legitimately stays on 'loading' or
          // 'slow' until the current refresh settles.
          //
          // Reporting failed() here is what made sustained panning, where each
          // refresh is superseded before it lands and none ever completes, walk
          // a perfectly healthy layer to 'unavailable' and tell the farmer "Not
          // available right now". That is the same wrong conclusion BBB-527 set
          // out to prevent, inverted.
          //
          // The adapter's own supersede check and this one move together: both
          // advance once per dispatch this loop makes, so a falsy result that
          // gets past here really did fail.
          if (generations.get(source.id) !== generation) return;

          const status = statusForRefresh(result);
          if (!status) {
            // A falsy result from the current refresh is the adapter's word for
            // "the fetch failed and nothing was repainted". Only a failure still
            // unanswered after ten seconds reaches 'unavailable', and a later
            // success clears it.
            tracker?.failed();
            return;
          }
          // succeeded() first, then the refresh's own status. succeeded()
          // publishes 'loaded', and 'partial' is the more specific truth: the
          // layer hit its page cap, so designations really are missing from the
          // render. Publishing in the other order would leave 'loaded' standing.
          tracker?.succeeded();
          setStatusRef.current?.(source.id, status);
        })
        // A rejecting adapter is the same case as a falsy result from the
        // current refresh: the fetch failed, so the previous render still stands
        // and only the tracker has anything to say about it. Caught per layer,
        // inside the loop, so one adapter throwing neither becomes an unhandled
        // rejection nor stops the others refreshing in parallel.
        .catch((err) => {
          // Same supersede rule as above for the tracker. The log is left
          // unconditional on purpose: an adapter that THROWS rather than
          // returning undefined has a defect of its own, and that is worth
          // seeing whether or not the viewport moved on.
          if (generations.get(source.id) === generation) tracker?.failed();
          reportWarn(`vector layer ${source.id} refresh failed`, err);
        });
    }
  }, [map]);

  // Reconcile which handles exist, then repaint. `desired` is identity-stable
  // across a zoom that does not change the selected set, so this no longer
  // duplicates the fetch the `idle` listener below is about to make.
  useEffect(() => {
    if (!map || !window.google?.maps) return;

    const layers = layersRef.current;
    const trackers = trackersRef.current;
    const desiredIds = new Set(desired.map((s) => s.id));

    for (const [id, handle] of [...layers]) {
      if (!desiredIds.has(id)) {
        handle.destroy();
        layers.delete(id);
        // Drop the health timers with the handle, or a layer turned off while it
        // was still loading would announce itself unavailable seconds later.
        trackers.get(id)?.dispose();
        trackers.delete(id);
      }
    }

    for (const source of desired) {
      if (!layers.has(source.id)) {
        trackers.set(
          source.id,
          createHealthTracker({
            // Through setStatusRef for the same reason refreshAll publishes that
            // way: a tracker built around the caller's setStatus identity would
            // put that dependency back into this effect and refetch every layer
            // on every zoom step (BBB-523).
            report: (status) => setStatusRef.current?.(source.id, status),
          }),
        );
        layers.set(
          source.id,
          createLayerForSource(source, {
            onStats: (stats) => recordLayer(source.id, stats),
          }),
        );
      }
    }

    refreshAll();
  }, [map, desired, refreshAll]);

  // Refetch when the user finishes panning or zooming.
  useEffect(() => {
    if (!map || !window.google?.maps) return undefined;
    const listener = map.addListener('idle', refreshAll);
    return () => listener?.remove?.();
  }, [map, refreshAll]);

  // Detach every polyline when the map goes away or the component unmounts, and
  // silence the trackers with them: a fetch still in flight will settle after
  // this, and a disposed tracker is the only thing stopping it publishing into a
  // panel that has gone.
  useEffect(() => {
    if (!map) return undefined;
    const layers = layersRef.current;
    const trackers = trackersRef.current;
    return () => {
      for (const handle of layers.values()) handle.destroy();
      layers.clear();
      for (const tracker of trackers.values()) tracker.dispose();
      trackers.clear();
    };
  }, [map]);
}
